// A document as the core's rows, and back.
//
// The core hands a document over as `DocumentValue` rows — a field, a kind,
// and the column the kind names — so that a Kotlin or Swift writer is told
// whether a number is an integer (the rules check `is int`) and where the
// server's sentinels go (core-ffi's `DocumentValue` has the table). This is the
// web's side of that: rows to the plain object `batch.set` takes, and a stored
// or composed object to rows for a core call that reads one.
//
// Shared by `people.js`, the calendar's documents and messaging, so it
// carries:
//
//   * the `arrayUnion` and `arrayRemove` kinds, built with the writer's own
//     sentinels — a removed occurrence's date, a tag's members;
//   * a field name with a dot in it, which is a key inside a map field:
//     `reminderLeadHours.email` and `.sms` are `reminderLeadHours: { email,
//     sms }`, the one map a category writes. (`batch.set` would store a
//     dotted key literally, so the nesting has to happen here.)
//
// And the two kinds a `messages` document needs (core-ffi's `DocumentValue`
// table has them):
//
//   * `map` — an empty map, which the dotted rows after it fill: a message's
//     `context`, which may be empty and which the rules check `is map`;
//   * `mapList` — a list of `integer` empty maps, filled by rows named
//     `to[0].name`, `to[0].address`, …: a message's `to` and `cc`.

const MEMBER = /^([^.[\]]+)\[(\d+)\]\.(.+)$/;

/// The core's rows as the plain object a write takes. A row of a sentinel
/// kind needs its sentinel handed in, and says so if it was not: a document
/// that silently wrote `undefined` where a server timestamp belongs would be
/// refused by the rules with nothing naming the field.
export function documentFromRows(rows, serverTimestamp = undefined, { arrayUnion, arrayRemove } = {}) {
  const out = {};
  const put = (field, value) => {
    // `to[0].name`: a key of one element of a `mapList` — matched before the
    // dot rule, or it would land as a map called `to[0]`.
    const member = MEMBER.exec(field);
    if (member) {
      const [, list, index, key] = member;
      if (!Array.isArray(out[list]) || !out[list][index]) {
        throw new Error(`documentFromRows: "${field}" names an element no mapList row made`);
      }
      out[list][index][key] = value;
      return;
    }
    const dot = field.indexOf('.');
    if (dot < 0) {
      out[field] = value;
      return;
    }
    const outer = field.slice(0, dot);
    out[outer] = { ...(out[outer] || {}), [field.slice(dot + 1)]: value };
  };
  const need = (sentinel, name, row) => {
    if (sentinel === undefined) {
      throw new Error(`documentFromRows: "${row.field}" is ${name} and no sentinel was handed in`);
    }
    return sentinel;
  };
  for (const row of rows) {
    switch (row.kind) {
      case 'null': put(row.field, null); break;
      case 'string': put(row.field, row.text); break;
      case 'boolean': put(row.field, row.flag); break;
      case 'integer': put(row.field, row.integer); break;
      case 'double': put(row.field, row.double); break;
      case 'stringList': put(row.field, row.items); break;
      case 'serverTimestamp':
        put(row.field, need(serverTimestamp, 'a server timestamp', row));
        break;
      case 'arrayUnion':
        put(row.field, need(arrayUnion, 'an arrayUnion', row)(...row.items));
        break;
      case 'arrayRemove':
        put(row.field, need(arrayRemove, 'an arrayRemove', row)(...row.items));
        break;
      // A map field, empty until the dotted rows after it fill it.
      case 'map':
        put(row.field, {});
        break;
      // A list of `integer` maps, filled by the `field[i].key` rows after it.
      case 'mapList':
        put(row.field, Array.from({ length: row.integer }, () => ({})));
        break;
      default:
        throw new Error(`documentFromRows: "${row.field}" has a kind this adapter does not know: ${row.kind}`);
    }
  }
  return out;
}

/// A stored or composed document as the rows the core reads — the inverse of
/// `documentFromRows`, and the shape a phone builds from its own snapshot. A
/// key the document lacks (or holds as `undefined`) is no row, which is how
/// `null !== undefined` survives the crossing; a value of a type no row holds
/// is kind `other`, which equals nothing.
export function documentToRows(doc = {}) {
  const rows = [];
  for (const [field, v] of Object.entries(doc)) {
    if (v === undefined) continue;
    const row = { field, kind: 'other', text: null, flag: false, integer: 0, double: 0, items: [] };
    if (v === null) row.kind = 'null';
    else if (typeof v === 'string') Object.assign(row, { kind: 'string', text: v });
    else if (typeof v === 'boolean') Object.assign(row, { kind: 'boolean', flag: v });
    else if (Number.isSafeInteger(v) && !Object.is(v, -0)) Object.assign(row, { kind: 'integer', integer: v });
    else if (typeof v === 'number') Object.assign(row, { kind: 'double', double: v });
    else if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
      Object.assign(row, { kind: 'stringList', items: v });
    }
    rows.push(row);
  }
  return rows;
}
